import { isEmpty } from 'lodash';
import { v4 } from 'uuid';
import { TASK_MODEL } from 'services/entity/task';
import { DEFAULT_EMPTY_VALUE } from '../../constants';
import { TASK_TYPE } from '../../constants/task';
import {
  getAssignedDepartmentList,
  getDepartmentNamesArray,
  getElapsedTime,
  getItemOrDefaultToValue,
} from '../../details';
import { getStatusValue, getUserStringForList } from '../../helper/task';
import { displayTime, formatDuration } from '../../time';
import { serviceTypes } from 'utils/constants/serviceTypes';
import { setSiteName } from 'utils/helper/site';

export const transform = (list, currentTime, globalDepartmentList = []) => {
  return list.map(data => ({
    [TASK_MODEL.id.id]: data.id,
    archived: data.archived,
    category: {
      id: getItemOrDefaultToValue(data, 'category', '-'),
      icon: getItemOrDefaultToValue(data, 'categoryIcon', '-'),
      name: getItemOrDefaultToValue(data, 'category', '-'),
    },
    [TASK_MODEL.taskName.id]: getItemOrDefaultToValue(
      data,
      TASK_MODEL.taskName.id,
      DEFAULT_EMPTY_VALUE,
    ),
    multiTaskName: data.name,
    [TASK_MODEL.taskType.id]:
      data[TASK_MODEL.taskType.id] || DEFAULT_EMPTY_VALUE,
    [TASK_MODEL.taskCategory.id]: data['categoryIcon'] || DEFAULT_EMPTY_VALUE,

    [TASK_MODEL.businessClient.name.field]: data.businessClient
      ? getItemOrDefaultToValue(
          data.businessClient,
          TASK_MODEL.businessClient.name.id,
          DEFAULT_EMPTY_VALUE,
        )
      : DEFAULT_EMPTY_VALUE,
    [TASK_MODEL.description.id]: data.description
      ? getItemOrDefaultToValue(
          data,
          TASK_MODEL.description.id,
          DEFAULT_EMPTY_VALUE,
        )
      : DEFAULT_EMPTY_VALUE,
    [TASK_MODEL.site.name.field]: data.site
      ? getItemOrDefaultToValue(
          data.site,
          TASK_MODEL.site.name.id,
          DEFAULT_EMPTY_VALUE,
        )
      : DEFAULT_EMPTY_VALUE,
    [TASK_MODEL.startedByName.id]: getItemOrDefaultToValue(
      data,
      TASK_MODEL.startedByName.id,
      DEFAULT_EMPTY_VALUE,
    ),
    [TASK_MODEL.finishedByName.id]: getItemOrDefaultToValue(
      data,
      TASK_MODEL.finishedByName.id,
      DEFAULT_EMPTY_VALUE,
    ),
    [TASK_MODEL.numSchedule.id]: getItemOrDefaultToValue(
      data,
      TASK_MODEL.numSchedule.id,
      DEFAULT_EMPTY_VALUE,
    ),
    [TASK_MODEL.numEvidence.id]: getItemOrDefaultToValue(
      data,
      TASK_MODEL.numEvidence.id,
      DEFAULT_EMPTY_VALUE,
    ),
    [TASK_MODEL.numSubtask.id]: getItemOrDefaultToValue(
      data,
      TASK_MODEL.numSubtask.id,
      DEFAULT_EMPTY_VALUE,
    ),
    [TASK_MODEL.timeStarted.id]: data[TASK_MODEL.timeStarted.id]
      ? displayTime({
          timestamp: getItemOrDefaultToValue(data, TASK_MODEL.timeStarted.id),
        })
      : DEFAULT_EMPTY_VALUE,

    [TASK_MODEL.timeEnded.id]: data[TASK_MODEL.timeEnded.id]
      ? displayTime({
          timestamp: getItemOrDefaultToValue(
            data,
            TASK_MODEL.timeEnded.id,
            DEFAULT_EMPTY_VALUE,
          ),
        })
      : DEFAULT_EMPTY_VALUE,

    [TASK_MODEL.elapsedTime.id]: data[TASK_MODEL.timeStarted.id]
      ? getElapsedTime(
          {
            currentStatus: data[TASK_MODEL.status.id],
            startTime: data[TASK_MODEL.timeStarted.id],
            currentStatusTimestamp: data[TASK_MODEL.timeEnded.id],
          },
          currentTime,
        )
      : '0s',

    [TASK_MODEL.status.id]: data.status
      ? getStatusValue(data, TASK_MODEL.status.id)
      : DEFAULT_EMPTY_VALUE,

    ...(data.surfaceU &&
      data.surfaceV && {
        surface: {
          id: data[TASK_MODEL.id.id],
          text: data[TASK_MODEL.taskName.id],
          lat: data[TASK_MODEL.surfaceV.id],
          lng: data[TASK_MODEL.surfaceU.id],
          icon: TASK_TYPE.CLEANING.mapIcon,
        },
      }),
    // this gives array of string names
    departments:
      data?.departmentIds && globalDepartmentList.length > 0
        ? getDepartmentNamesArray(globalDepartmentList, data)
        : [],
    // this give array of objects with string name and ids
    assignedDepartmentList:
      data?.departmentIds && globalDepartmentList.length > 0
        ? getAssignedDepartmentList(globalDepartmentList, data)
        : [],
  }));
};

const setIconBasedOnCategory = category => {
  switch (category) {
    case serviceTypes.maintenance:
      return TASK_TYPE.MAINTENANCE.mapIcon;
    case serviceTypes.security:
      return TASK_TYPE.SECURITY.mapIcon;
    default:
      return TASK_TYPE.CLEANING.mapIcon;
  }
};

export const transformOperational = (
  list,
  currentTime,
  users,
  globalDepartmentList,
  pageName,
) => {
  const bizClientList = list.map(m => m.businessClient);
  return list.map(data => {
    const hasId = data.id ? data.id : v4();
    const isDummyTask = isEmpty(data.id);
    const newSite = {
      ...data.site,
      businessClientName: data.businessClient?.businessClientName,
      businessClientId: data.businessClient.id,
    };
    const formatedSiteName = setSiteName(newSite, bizClientList);

    return {
      pageName,
      archived: data.archived,
      [TASK_MODEL.id.id]: hasId,
      [TASK_MODEL.assigneeFullName.field]: data.assigneeId
        ? users[data[TASK_MODEL.assignee.id]] || DEFAULT_EMPTY_VALUE
        : data.schedulePatterns
        ? getUserStringForList(users, data.schedulePatterns[0].assignToUserIDs)
        : DEFAULT_EMPTY_VALUE,
      isDummyTask: isDummyTask,
      [TASK_MODEL.taskName.id]: getItemOrDefaultToValue(
        data,
        TASK_MODEL.taskName.id,
        DEFAULT_EMPTY_VALUE,
      ),
      [TASK_MODEL.taskType.id]: getItemOrDefaultToValue(
        data,
        TASK_MODEL.taskType.id,
        DEFAULT_EMPTY_VALUE,
      ),
      [TASK_MODEL.taskCategory.id]: data['categoryIcon'] || DEFAULT_EMPTY_VALUE,

      [TASK_MODEL.businessClient.name.field]: data.businessClient
        ? getItemOrDefaultToValue(
            data.businessClient,
            TASK_MODEL.businessClient.name.id,
            DEFAULT_EMPTY_VALUE,
          )
        : DEFAULT_EMPTY_VALUE,
      multiTaskName: data.name,
      [TASK_MODEL.description.id]: data.description
        ? getItemOrDefaultToValue(
            data,
            TASK_MODEL.description.id,
            DEFAULT_EMPTY_VALUE,
          )
        : DEFAULT_EMPTY_VALUE,
      [TASK_MODEL.site.name.field]: data.site
        ? getItemOrDefaultToValue(
            data.site,
            TASK_MODEL.site.name.id,
            DEFAULT_EMPTY_VALUE,
          )
        : DEFAULT_EMPTY_VALUE,
      [TASK_MODEL.schedule.startAfter.id]:
        data.schedule && data.schedule.startAfter
          ? displayTime({
              timestamp: getItemOrDefaultToValue(
                data.schedule,
                TASK_MODEL.schedule.startAfter.id,
                DEFAULT_EMPTY_VALUE,
              ),
            })
          : DEFAULT_EMPTY_VALUE,
      [TASK_MODEL.schedule.finishBefore.id]:
        data.schedule && data.schedule.finishBefore
          ? displayTime({
              timestamp: getItemOrDefaultToValue(
                data.schedule,
                TASK_MODEL.schedule.finishBefore.id,
                DEFAULT_EMPTY_VALUE,
              ),
            })
          : DEFAULT_EMPTY_VALUE,
      [TASK_MODEL.expectedDuration.id]: data.expectedDuration
        ? formatDuration(data.expectedDuration)
        : DEFAULT_EMPTY_VALUE,
      [TASK_MODEL.startedByName.id]: getItemOrDefaultToValue(
        data,
        TASK_MODEL.startedByName.id,
        DEFAULT_EMPTY_VALUE,
      ),
      [TASK_MODEL.finishedByName.id]: getItemOrDefaultToValue(
        data,
        TASK_MODEL.finishedByName.id,
        DEFAULT_EMPTY_VALUE,
      ),
      [TASK_MODEL.numEvidence.id]: getItemOrDefaultToValue(
        data,
        TASK_MODEL.numEvidence.id,
        DEFAULT_EMPTY_VALUE,
      ),
      [TASK_MODEL.numSubtask.id]: getItemOrDefaultToValue(
        data,
        TASK_MODEL.numSubtask.id,
        DEFAULT_EMPTY_VALUE,
      ),
      [TASK_MODEL.timeStarted.id]: data[TASK_MODEL.timeStarted.id]
        ? displayTime({
            timestamp: getItemOrDefaultToValue(data, TASK_MODEL.timeStarted.id),
          })
        : DEFAULT_EMPTY_VALUE,

      [TASK_MODEL.timeEnded.id]: data[TASK_MODEL.timeEnded.id]
        ? displayTime({
            timestamp: getItemOrDefaultToValue(
              data,
              TASK_MODEL.timeEnded.id,
              DEFAULT_EMPTY_VALUE,
            ),
          })
        : DEFAULT_EMPTY_VALUE,

      [TASK_MODEL.elapsedTime.id]: data[TASK_MODEL.timeStarted.id]
        ? getElapsedTime(
            {
              currentStatus: data[TASK_MODEL.status.id],
              startTime: data[TASK_MODEL.timeStarted.id],
              currentStatusTimestamp: data[TASK_MODEL.timeEnded.id],
            },
            currentTime,
          )
        : '0s',
      [TASK_MODEL.templateId.id]: getItemOrDefaultToValue(
        data,
        TASK_MODEL.templateId.id,
        DEFAULT_EMPTY_VALUE,
      ),
      [TASK_MODEL.status.id]: data.status
        ? getStatusValue(data, TASK_MODEL.status.id)
        : DEFAULT_EMPTY_VALUE,

      ...(data.surfaceU &&
        data.surfaceV && {
          surface: {
            id: hasId,
            text: data[TASK_MODEL.taskName.id],
            lat: data[TASK_MODEL.surfaceV.id],
            lng: data[TASK_MODEL.surfaceU.id],
            icon: setIconBasedOnCategory(data.taskCategory),
          },
        }),
      surfaceId: data.surfaceId || null,
      schedule: data.schedule,
      schedulePattern: data.schedulePattern,
      businessClient: data.businessClient,
      site: { ...data?.site, name: formatedSiteName }, // replace actual site name with formated name
      // this gives array of string names
      departments:
        data?.departmentIds && globalDepartmentList.length > 0
          ? getDepartmentNamesArray(globalDepartmentList, data)
          : [],
      // this give array of objects with string name and ids
      assignedDepartmentList:
        data?.departmentIds && globalDepartmentList.length > 0
          ? getAssignedDepartmentList(globalDepartmentList, data)
          : [],
    };
  });
};
